// ToastProvider.tsx — wraps the Radix Toast.Provider + manages a queue.
//
// Plan ref: 06-06-PLAN-cmd-k-toast-empty-loading-error.md §Task 2 step 1.
//
// Architecture:
//   - Module-level queue lives in toast-store.ts so useToast() can be called
//     from non-React contexts (mutation handlers, error boundaries, etc.).
//   - The Provider subscribes via useSyncExternalStore so React renders the
//     visible toasts in their proper order; unmount = no listeners.
//   - Queue cap: 3. The 4th push evicts the oldest (FIFO).
//   - The Viewport positions toasts bottom-right and stacks them up.

import { useSyncExternalStore, type ReactNode } from 'react';
import * as RToast from '@radix-ui/react-toast';
import { Toast } from './Toast';
import { getEntries, subscribe, dismiss } from './toast-store';

export interface ToastProviderProps {
  children: ReactNode;
  /** Override per-test (Radix Toast.Provider swipe direction etc.) — optional. */
  duration?: number;
}

export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
  const entries = useSyncExternalStore(subscribe, getEntries, getEntries);

  return (
    <RToast.Provider swipeDirection="right" duration={5_000}>
      {children}
      {entries.map((e) => (
        <Toast key={e.id} entry={e} onDismiss={dismiss} />
      ))}
      <RToast.Viewport
        className="fixed bottom-4 right-4 z-overlay flex max-h-screen w-[min(420px,calc(100vw-2rem))] flex-col-reverse gap-2 outline-none"
        // Toasts are stack ordered most-recent at the bottom of the list
        // (visually on top of the viewport). flex-col-reverse achieves this
        // without re-sorting the queue.
      />
    </RToast.Provider>
  );
}
